import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { API_URL } from '../config';

const POLL_INTERVAL_MS = 60 * 1000; // každou minutu

const cursorKey = (userId) => `leadCursor_${userId}`;

const formatDateTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * NewLeadNotifier — globální komponenta, která každou minutu kontroluje nové leady
 * přiřazené aktuálnímu uživateli a zobrazuje modal "Nový lead".
 *
 * - Aktivní obchodník: nový lead → modal "🔔 Nový lead" (Jméno, IČO, od koho, poslední aktivita)
 * - Přihlášení po nečinnosti: nejdřív se počká na uvítací modal (welcomeActive), pak se
 *   zobrazí seznam "Během vaší nečinnosti vám byly přiděleny následující leady".
 */
export default function NewLeadNotifier({ currentUserId, isDarkMode, userRole, welcomeActive, onOpenLead }) {
  const [queue, setQueue] = useState([]);     // leady čekající na zobrazení
  const [mode, setMode] = useState('live');    // 'live' | 'inactivity'
  const cursorRef = useRef(null);
  const initializedRef = useRef(false);

  const isEligible = currentUserId && userRole !== 'callcentrum' && userRole !== 'superadmin';

  const advanceCursor = useCallback((serverTime) => {
    if (!serverTime) return;
    cursorRef.current = serverTime;
    try { localStorage.setItem(cursorKey(currentUserId), serverTime); } catch (_) { /* ignore */ }
  }, [currentUserId]);

  const fetchLeads = useCallback(async (since) => {
    let url = `${API_URL}/my-new-leads?userId=${currentUserId}`;
    if (since) url += `&since=${encodeURIComponent(since)}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, [currentUserId]);

  // Inicializace + polling
  useEffect(() => {
    if (!isEligible) return;
    let cancelled = false;
    let intervalId = null;
    let inactivityTimer = null;

    const runInitial = async () => {
      const storedCursor = (() => {
        try { return localStorage.getItem(cursorKey(currentUserId)); } catch (_) { return null; }
      })();

      try {
        const data = await fetchLeads(storedCursor);
        if (cancelled) return;

        if (!storedCursor) {
          // První spuštění na tomto zařízení – nespamovat starými leady, jen nastav kurzor
          advanceCursor(data.serverTime);
        } else if (Array.isArray(data.leads) && data.leads.length > 0) {
          // Leady přidělené během nečinnosti — počkej chvíli, aby se nejdřív stihl
          // objevit uvítací (denní) modal, který má přednost.
          inactivityTimer = setTimeout(() => {
            if (cancelled) return;
            setMode('inactivity');
            setQueue(data.leads);
          }, 2500);
          advanceCursor(data.serverTime);
        } else {
          advanceCursor(data.serverTime);
        }
      } catch (_) {
        // tiše ignoruj – zkusí se znovu v intervalu
      } finally {
        initializedRef.current = true;
      }
    };

    const poll = async () => {
      if (!initializedRef.current) return;
      try {
        const data = await fetchLeads(cursorRef.current);
        if (cancelled) return;
        if (Array.isArray(data.leads) && data.leads.length > 0) {
          setQueue(prev => {
            // Přidej jen leady, které ještě nejsou ve frontě
            const existingIds = new Set(prev.map(l => l.transferId));
            const fresh = data.leads.filter(l => !existingIds.has(l.transferId));
            if (fresh.length === 0) return prev;
            // Živé leady mají přednost jako "Nový lead"
            setMode('live');
            return [...fresh, ...prev];
          });
        }
        advanceCursor(data.serverTime);
      } catch (_) {
        // tiše ignoruj
      }
    };

    runInitial();
    intervalId = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      if (inactivityTimer) clearTimeout(inactivityTimer);
    };
  }, [isEligible, currentUserId, fetchLeads, advanceCursor]);

  if (!isEligible) return null;
  if (queue.length === 0) return null;
  // Počkej, než se zavře uvítací (denní) modal
  if (welcomeActive) return null;

  const handleClose = () => setQueue([]);

  const handleOpen = (lead) => {
    if (lead.companyId && onOpenLead) onOpenLead(lead.companyId);
    setQueue(prev => prev.filter(l => l.transferId !== lead.transferId));
  };

  const cardBg = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const subText = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const strongText = isDarkMode ? 'text-white' : 'text-gray-900';

  const renderLeadCard = (lead) => (
    <div key={lead.transferId} className={`rounded-lg border p-3 ${cardBg}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className={`font-semibold text-sm truncate ${strongText}`}>{lead.name}</div>
          <div className={`text-xs mt-0.5 ${subText}`}>
            IČO: <span className="font-medium">{lead.ico || '—'}</span>
          </div>
          {lead.fromUserName && (
            <div className={`text-xs mt-0.5 ${subText}`}>
              Od: <span className="font-medium">{lead.fromUserName}</span>
            </div>
          )}
          {lead.lastActivity && (lead.lastActivity.text || lead.lastActivity.byName) && (
            <div className={`mt-2 text-xs rounded p-2 ${isDarkMode ? 'bg-gray-900/60' : 'bg-gray-50'}`}>
              <div className={`font-medium mb-0.5 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                📞 Poslední aktivita{lead.lastActivity.byName ? ` (${lead.lastActivity.byName})` : ''}
              </div>
              {lead.lastActivity.text && <div className={strongText}>{lead.lastActivity.text}</div>}
              {lead.lastActivity.createdAt && (
                <div className={`mt-0.5 ${subText}`}>{formatDateTime(lead.lastActivity.createdAt)}</div>
              )}
            </div>
          )}
        </div>
        <button
          onClick={() => handleOpen(lead)}
          className="flex-shrink-0 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium transition-colors"
        >
          Otevřít
        </button>
      </div>
    </div>
  );

  const isInactivity = mode === 'inactivity';
  const title = isInactivity ? '👋 Vítejte zpět!' : '🔔 Nový lead';
  const subtitle = isInactivity
    ? 'Během vaší nečinnosti vám byly přiděleny následující leady:'
    : (queue.length > 1 ? `Byly vám přiděleny ${queue.length} nové leady:` : 'Byl vám přidělen nový lead:');

  const modal = (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4">
      <div className={`w-full max-w-md rounded-xl shadow-2xl ${isDarkMode ? 'bg-gray-900' : 'bg-white'} max-h-[85vh] flex flex-col`}>
        <div className={`px-5 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <h3 className={`text-lg font-bold ${strongText}`}>{title}</h3>
          <p className={`text-sm mt-0.5 ${subText}`}>{subtitle}</p>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          {queue.map(lead => renderLeadCard(lead))}
        </div>

        <div className={`px-5 py-3 border-t flex justify-end ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={handleClose}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
          >
            Zavřít
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
